import React, { useEffect, useState } from 'react';

import { type QtiQuestionDTO, QuestionRenderer } from '@/src/lib';
import {
    SimpleSingleSelect,
    SimpleTextField,
    SimpleButton,
    Loading,
    ErrorMessage,
    SimpleMultipleSelect,
} from '@/src/ui';
import {
    questionTypes1,
    questionTypes2,
    questionTypes3,
    type QuestionTypeOption,
    type NumberParam,
    type SelectParam,
    type MultiSelectParam,
    type DynamicQuestionResponse,
} from './dtos';
import apiRequest from '@/src/services/axiosClient';

const DynamicSandboxPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [question, setQuestion] = useState<QtiQuestionDTO | null>(null);
    const [questionType, setQuestionType] = useState(1);
    const [questionSubType, setQuestionSubType] = useState(1);

    const [params, setParams] = useState<Record<string, any>>({});

    const questionTypeOptions: QuestionTypeOption[] = [
        ...questionTypes1,
        ...questionTypes2,
        ...questionTypes3,
    ];

    function setParamDefaultValues(
        params: (NumberParam | SelectParam | MultiSelectParam)[] | undefined
    ) {
        if (params) {
            params.forEach((param) => {
                if (param.type === 'number') {
                    const numParam = param as NumberParam;
                    const newVal =
                        numParam.default !== undefined
                            ? numParam.default
                            : Math.floor((numParam.min + numParam.max) / 2);
                    setParams((prev) => ({ ...prev, [numParam.name]: newVal }));
                } else if (param.type === 'select') {
                    const selectParam = param as SelectParam;
                    const firstOptionId = selectParam.options[0]?.id;
                    setParams((prev) => ({
                        ...prev,
                        [selectParam.name]: firstOptionId === undefined ? 0 : firstOptionId,
                    }));
                } else if (param.type === 'multiSelect') {
                    const multiSelectParam = param as MultiSelectParam;
                    setParams((prev) => ({ ...prev, [multiSelectParam.name]: [] }));
                }
            });
        }
    }

    function handleSetQuestionType(id: number) {
        setError(null);
        setQuestion(null);
        setParams({});
        const selectedOption = questionTypeOptions.find((opt) => opt.id === id);
        setParamDefaultValues(selectedOption?.params);
        setQuestionType(id);
        setQuestionSubType(1);
    }
    function handleSetQuestionSubType(id: number) {
        setError(null);
        setQuestion(null);
        const selectedOption = questionTypeOptions.find((opt) => opt.id === questionType);
        const selectedSubType = selectedOption?.subTypes?.find((sub) => sub.id === id);
        setParamDefaultValues(selectedSubType?.params);
        setQuestionSubType(id);
    }

    function getQuestionSubTypes(id: number) {
        const option = questionTypeOptions.find((opt) => opt.id === id);
        return option && option.subTypes ? option.subTypes : [];
    }

    // Get selected type and params for rendering
    const selectedOption = questionTypeOptions.find((opt) => opt.id === questionType) || {
        id: 0,
        key: 'unknown',
        name: 'Unknown',
    };
    const typeParams = selectedOption.params || [];
    const subTypeParams = selectedOption.subTypes?.[questionSubType - 1]?.params || [];
    const currentParams = [...typeParams, ...subTypeParams];

    // Initialize defaults
    useEffect(() => {
        setParamDefaultValues(currentParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getNewQuestion = async () => {
        setLoading(true);
        setError(null);

        if (selectedOption.id === 0) {
            setLoading(false);
            setError('Invalid question type selected');
            return;
        }

        let paramError = '';
        const isParamInvalid = currentParams.some((param) => {
            if (param.type === 'number') {
                const numParam = param as NumberParam;
                if (
                    numParam.increment &&
                    params[numParam.name] % numParam.increment >= numParam.increment
                ) {
                    paramError = `${numParam.displayName || numParam.name} must be in increments of ${numParam.increment}`;
                    return true;
                }
                if (isNaN(params[numParam.name])) {
                    paramError = `${numParam.displayName || numParam.name} must be a valid number`;
                    return true;
                }
                if (params[numParam.name] < numParam.min || params[numParam.name] > numParam.max) {
                    paramError = `${numParam.displayName || numParam.name} must be between ${numParam.min} and ${numParam.max}`;
                    return true;
                }
            } else if (param.type === 'multiSelect') {
                const multiSelectParam = param as MultiSelectParam;
                if (!multiSelectParam.required) {
                    return false;
                }
                if (params[multiSelectParam.name].length === 0) {
                    paramError = `At least one option must be selected for ${multiSelectParam.displayName || multiSelectParam.name}`;
                    return true;
                }
            }
            // Add select param validation if needed
            return false;
        });

        if (isParamInvalid) {
            setLoading(false);
            setError(paramError);
            return;
        }

        // Set params based on type and subtype
        const paramsToSend = { ...params };

        if (questionSubType) {
            paramsToSend['type'] = questionSubType;
        }

        // Get question
        try {
            const res = await apiRequest(
                'POST',
                `/assessments/questions/dynamic/${selectedOption.key}`,
                paramsToSend
            );
            const typedData = res.data as DynamicQuestionResponse;
            const newQuestion = typedData.question;
            newQuestion.uuid = typedData.uuid;
            setQuestion(newQuestion);
        } catch (e: any) {
            setError(
                'Server error: ' +
                    (e.response?.data?.error || 'An error occurred while fetching the question')
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                maxWidth: 500,
                margin: '2rem auto',
                padding: 24,
                border: '1px solid #ccc',
                borderRadius: 8,
                background: '#fff',
            }}
        >
            <h2 style={{ marginBottom: 24 }}>Dynamic Question Preview</h2>
            <div style={{ marginBottom: 16 }}>
                <SimpleSingleSelect
                    name="questionType"
                    label="Question Type"
                    value={questionType}
                    onChange={(e) => handleSetQuestionType(Number(e.target.value))}
                    options={questionTypeOptions}
                />
            </div>
            {getQuestionSubTypes(questionType).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <SimpleSingleSelect
                        name="questionSubType"
                        label="SubType"
                        value={questionSubType}
                        onChange={(e) => handleSetQuestionSubType(Number(e.target.value))}
                        options={getQuestionSubTypes(questionType)}
                    />
                </div>
            )}
            {currentParams.map((param) => {
                if (param.type === 'number') {
                    param = param as NumberParam;
                    return (
                        <div key={param.name} style={{ marginBottom: 16 }}>
                            <SimpleTextField
                                name={param.name}
                                label={param.displayName || param.name}
                                value={String(params[param.name])}
                                onChange={(e) => {
                                    setParams((prev) => ({
                                        ...prev,
                                        [param.name]: e.target.value,
                                    }));
                                }}
                                // textFieldType="number"
                            />
                            {param.min !== undefined && param.max !== undefined && (
                                <div style={{ fontSize: 12, color: '#666' }}>
                                    (Min: {param.min}, Max: {param.max}, Increment:{' '}
                                    {param.increment || 1})
                                </div>
                            )}
                            {param.helpText && (
                                <div style={{ fontSize: 12, color: '#666' }}>{param.helpText}</div>
                            )}
                        </div>
                    );
                } else if (param.type === 'select') {
                    param = param as SelectParam;
                    return (
                        <div key={param.name} style={{ marginBottom: 16 }}>
                            <SimpleSingleSelect
                                name={param.name}
                                label={param.displayName || param.name}
                                value={params[param.name]} // You can manage select state similarly to number params
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setParams((prev) => ({ ...prev, [param.name]: val }));
                                }}
                                options={param.options}
                            />
                        </div>
                    );
                } else if (param.type === 'multiSelect') {
                    param = param as MultiSelectParam;
                    return (
                        <div key={param.name} style={{ marginBottom: 16 }}>
                            <SimpleMultipleSelect
                                name={param.name}
                                label={param.displayName || param.name}
                                value={params[param.name]} // You can manage multi-select state similarly to number params
                                onChange={(val) => {
                                    setParams((prev) => ({ ...prev, [param.name]: val }));
                                }}
                                options={param.options}
                            />
                        </div>
                    );
                }
                return null;
            })}
            <SimpleButton onClick={getNewQuestion} sx={{ mb: 2 }}>
                Generate New Question
            </SimpleButton>
            {loading && <Loading />}
            {error && <ErrorMessage error={error} />}
            {question && (
                <React.Fragment key={`question-${question.uuid}`}>
                    <QuestionRenderer
                        question={question}
                        stimMode="preview"
                        options={{
                            showScoring: true,
                        }}
                    />
                </React.Fragment>
            )}
        </div>
    );
};

export default DynamicSandboxPage;
